import type { Archive } from "../domain/types";
import { WEAPON_TYPES } from "../domain/types";
import { calculateDerived } from "../domain/equipmentStats";
import { profileFromCreation } from "../features/character-creation/catalog";
import {
  normalizeMhCharacter,
  normalizeMonsterIndividual,
} from "../domain/actors";
export interface RepairReport {
  archive: Archive;
  repairs: string[];
}
export function repairArchive(input: Archive): RepairReport {
  const archive = structuredClone(input) as Archive,
    repairs: string[] = [];
  if (!archive.archiveId) {
    archive.archiveId = crypto.randomUUID();
    repairs.push("为旧存档补全 archiveId");
  }
  if (!archive.player.profile) {
    const origin = archive.player.identity.origin || "旧大陆";
    archive.player.profile = profileFromCreation({
      archiveName: archive.name,
      characterName: archive.player.identity.name,
      gender: "male",
      raceId: "human",
      professionId: "hunter",
      birthplaceId: "kokoto",
      background: `${archive.player.identity.name}的旧版存档迁移而来；原出身记录：${origin}`,
      hook: "继续尚未完成的猎人旅程。",
    });
    archive.player.profile.gender = "unspecified";
    repairs.push("以人类猎人兼容配置迁移旧版角色资料");
  }
  archive.openingContext ??= `种族=${archive.player.profile.race.label}\n职业=${archive.player.profile.profession.label}\n出生地=${archive.player.profile.birthplace.label}\n背景=${archive.player.profile.background}\n开局钩子=${archive.player.profile.hook}`;
  archive.quickChat ??= { conversations: {}, pendingConversationIds: [] };
  archive.quickChat.conversations ??= {};
  archive.quickChat.pendingConversationIds ??= [];
  archive.plotEvolution ??= {
    guidance: "",
    guidanceUpdatedTurn: 0,
    directorLedger: "",
    currentDirective: "",
    updatedAt: null,
  };
  archive.meta.version = 4;
  archive.tasks ??= {
    available: [],
    activeTaskId: null,
    history: [],
    lastRefreshDay: archive.gameTime?.day ?? 1,
  };
  archive.tasks.available ??= [];
  archive.tasks.history ??= [];
  const active = archive.tasks.available.filter(
    (task) =>
      task.status === "active" ||
      (task.id === archive.tasks.activeTaskId && task.status === "completed"),
  );
  if (active.length > 1) {
    const keep =
      (archive.tasks.activeTaskId &&
        active.find((task) => task.id === archive.tasks.activeTaskId)) ||
      active[0];
    for (const task of active)
      if (task !== keep) {
        task.status = "available";
        repairs.push(`任务 ${task.id} 从重复 active 降级为 available`);
      }
    archive.tasks.activeTaskId = keep?.id ?? null;
  }
  if (
    archive.tasks.activeTaskId &&
    !archive.tasks.available.some(
      (task) => task.id === archive.tasks.activeTaskId,
    )
  ) {
    repairs.push("清除悬空 activeTaskId");
    archive.tasks.activeTaskId = null;
  }
  archive.itemRegistry ??= { version: 1, definitions: {}, lots: {} };
  archive.itemRegistry.definitions ??= {};
  archive.itemRegistry.lots ??= {};
  for (const [key, lots] of Object.entries(archive.itemRegistry.lots)) {
    archive.itemRegistry.lots[key] = (lots ?? []).filter(
      (lot) => Number.isFinite(lot.quantity) && lot.quantity > 0,
    );
    if (!archive.itemRegistry.definitions[key]) {
      delete archive.itemRegistry.lots[key];
      repairs.push(`移除无定义物品批次 ${key}`);
    }
  }
  for (const weapon of WEAPON_TYPES)
    if (!Number.isFinite(archive.player.proficiency[weapon])) {
      archive.player.proficiency[weapon] = 0;
      repairs.push(`补全 ${weapon} 熟练度`);
    }
  archive.player.hunter.hr = Math.max(
    1,
    Math.min(9, Math.trunc(archive.player.hunter.hr || 1)),
  );
  archive.player.hunter.hrExp = Math.max(0, archive.player.hunter.hrExp || 0);
  archive.player.derived = calculateDerived(
    archive.player,
    archive.itemRegistry,
  );
  archive.narrativeMemory ??= { summary: "", facts: [], lastCompressedTurn: 0 };
  archive.deepSummary ??= {
    text: "",
    coveredThroughMessageId: null,
    coveredTurns: 0,
    updatedAt: null,
    revision: 0,
  };
  archive.messages ??= [];
  archive.characters ??= {};
  archive.monsters ??= {};
  archive.characters = Object.fromEntries(
    Object.entries(archive.characters).map(([id, character]) => [
      id,
      normalizeMhCharacter(id, character, archive.scene?.location ?? ""),
    ]),
  );
  archive.monsters = Object.fromEntries(
    Object.entries(archive.monsters).map(([id, monster]) => [
      id,
      normalizeMonsterIndividual(id, monster),
    ]),
  );
  return { archive, repairs };
}
